import { useState, useCallback } from "react";

/**
 * Takes an object, returns three items in an array:
 * 1. the current object
 * 2. a merge function that combines a passed object with the current object
 * 3. a set function to entirely replace the object
 *
 * @example
 * const [data, mergeData, setData] = useMerge({ some: "thing", aNumber: 12345 });
 *
 * console.log(data);
 * // { some: "thing", aNumber: 12345 }
 *
 * mergeData({ some: "other" });
 *
 * // on the next render...
 * console.log(data);
 * // { some: "other", aNumber: 12345 }
 */
export function useMerge(initialState) {
  if (typeof initialState !== "object") {
    throw new TypeError(
      `useMerge requires an object for initial state but received \`${typeof initialState}\``
    );
  }

  const [state, setState] = useState(initialState);

  /**
   * Takes an object with one or more properties and merges it with the existing state.
   */
  const mergeState = useCallback(
    (patch) => void setState({ ...state, ...patch }),
    [state]
  );

  return [state, mergeState, setState];
}
